import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DocumentViewModalProps {
  document: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocumentViewModal({ document, open, onOpenChange }: DocumentViewModalProps) {
  if (!document) return null;

  const handleDownload = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('fleet-documents')
        .download(document.file_path);
      
      if (error) throw error;
      
      const url = URL.createObjectURL(data);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.name;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success("Download iniciado");
    } catch (error) {
      console.error("Erro ao baixar arquivo:", error);
      toast.error("Erro ao baixar arquivo");
    }
  };

  const handleViewFile = async () => {
    try {
      const { data } = supabase.storage
        .from('fleet-documents')
        .getPublicUrl(document.file_path);
      
      window.open(data.publicUrl, '_blank');
    } catch (error) {
      console.error("Erro ao abrir arquivo:", error);
      toast.error("Erro ao abrir arquivo");
    }
  };

  const getStatusBadge = () => {
    if (!document.expiry_date) {
      return <Badge variant="secondary">Sem vencimento</Badge>;
    }
    
    const today = new Date();
    const expiryDate = new Date(document.expiry_date);
    const daysToExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysToExpiry < 0) {
      return <Badge variant="destructive">Vencido</Badge>;
    } else if (daysToExpiry <= 30) {
      return <Badge className="bg-yellow-500">Vence em breve</Badge>;
    }
    return <Badge className="bg-green-500">Válido</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{document.name}</span>
            {getStatusBadge()}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações Básicas */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Informações Básicas</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Tipo:</span>
                <p className="font-medium">{document.document_type}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Categoria:</span>
                <p className="font-medium capitalize">{document.category}</p>
              </div>
            </div>
          </div>

          {/* Vinculação */}
          {(document.fleet_vehicles || document.fleet_drivers) && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Vinculação</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {document.fleet_vehicles && (
                  <div>
                    <span className="text-muted-foreground">Veículo:</span>
                    <p className="font-medium">
                      {document.fleet_vehicles.plate} - {document.fleet_vehicles.make} {document.fleet_vehicles.model}
                    </p>
                  </div>
                )}
                {document.fleet_drivers && (
                  <div>
                    <span className="text-muted-foreground">Motorista:</span>
                    <p className="font-medium">{document.fleet_drivers.name}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dados do Documento */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Dados do Documento</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {document.reference_number && (
                <div>
                  <span className="text-muted-foreground">Número/Referência:</span>
                  <p className="font-medium">{document.reference_number}</p>
                </div>
              )}
              {document.authority && (
                <div>
                  <span className="text-muted-foreground">Órgão Emissor:</span>
                  <p className="font-medium">{document.authority}</p>
                </div>
              )}
              {document.issue_date && (
                <div>
                  <span className="text-muted-foreground">Data de Emissão:</span>
                  <p className="font-medium">
                    {format(new Date(document.issue_date), "PPP", { locale: ptBR })}
                  </p>
                </div>
              )}
              {document.expiry_date && (
                <div>
                  <span className="text-muted-foreground">Data de Vencimento:</span>
                  <p className="font-medium">
                    {format(new Date(document.expiry_date), "PPP", { locale: ptBR })}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Informações do Arquivo */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Arquivo</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Tipo:</span>
                <p className="font-medium">{document.mime_type?.split('/')[1].toUpperCase()}</p>
              </div>
              {document.file_size && (
                <div>
                  <span className="text-muted-foreground">Tamanho:</span>
                  <p className="font-medium">
                    {(document.file_size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Ações */}
          <div className="flex gap-2 pt-4 border-t">
            <Button onClick={handleDownload} className="flex-1">
              <Download className="h-4 w-4 mr-2" />
              Baixar
            </Button>
            <Button onClick={handleViewFile} variant="outline" className="flex-1">
              <ExternalLink className="h-4 w-4 mr-2" />
              Abrir em Nova Aba
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
